import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PropTypes from "prop-types";
import * as React from "react";
import { moveForDrop } from "../../../domain/productComposer/images";
import {
  AddIcon,
  Badge,
  Button,
  IconButton,
  Menu,
  MoreVertIcon,
  Progress,
  Typography,
} from "../../../themeV2";
import css from "./ImageStrip.module.css";

const MAX_IMAGES = 10;
const ACCEPTED = "image/png, image/jpeg";
const BUSY = new Set(["preparing", "uploading"]);

/* A touch drag starts only after a press, so a swipe over the images still scrolls the page. */
const TOUCH_DRAG = { delay: 250, tolerance: 5 };
const MOUSE_DRAG = { distance: 8 };

const nameOf = (index) => (index === 0 ? "main image" : `image ${index + 1}`);

const ImageActions = function ImageActions({
  image,
  index,
  count,
  canEdit,
  onEdit,
  onMove,
  onRemove,
}) {
  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <IconButton
            variant="filled"
            size="sm"
            label={`Actions for ${nameOf(index)}`}
          />
        }
      >
        <MoreVertIcon />
      </Menu.Trigger>
      <Menu.Popup side="bottom" align="end">
        {index > 0 && (
          <Menu.Item onClick={() => onMove(index, 0)}>Make main</Menu.Item>
        )}
        {index > 0 && (
          <Menu.Item onClick={() => onMove(index, index - 1)}>
            Move earlier
          </Menu.Item>
        )}
        {index < count - 1 && (
          <Menu.Item onClick={() => onMove(index, index + 1)}>
            Move later
          </Menu.Item>
        )}
        {canEdit && (
          <Menu.Item onClick={() => onEdit(image.imageId)}>Edit</Menu.Item>
        )}
        <Menu.Item onClick={() => onRemove(image.imageId)}>Remove</Menu.Item>
      </Menu.Popup>
    </Menu.Root>
  );
};

ImageActions.propTypes = {
  canEdit: PropTypes.bool,
  count: PropTypes.number.isRequired,
  image: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ImageStatus = function ImageStatus({ image, name, onRetry }) {
  return BUSY.has(image.status) ? (
    <div className={css.scrim}>
      <Progress
        variant="circular"
        size="sm"
        value={image.percent ?? null}
        label={`Uploading ${name}`}
      />
    </div>
  ) : image.status === "failed" ? (
    <div className={css.errorScrim}>
      <Typography variant="label-md" as="p" role="alert" className={css.error}>
        {image.error ?? "Upload failed"}
      </Typography>
      <Button size="sm" variant="filled" onClick={() => onRetry(image.imageId)}>
        Retry
      </Button>
    </div>
  ) : image.status === "too-small" ? (
    <div className={css.errorScrim}>
      <Typography variant="label-md" as="p" role="alert">
        Too small
      </Typography>
    </div>
  ) : null;
};

ImageStatus.propTypes = {
  image: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

const Thumbnail = function Thumbnail({
  image,
  index,
  count,
  readOnly,
  canEdit,
  onEdit,
  onMove,
  onRemove,
  onRetry,
}) {
  const name = nameOf(index);
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image.imageId, disabled: readOnly });

  return (
    <li
      ref={setNodeRef}
      className={css.item}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div className={css.frame} {...listeners}>
        <img
          className={css.image}
          src={image.imageUrl ?? image.previewUrl}
          alt={name[0].toUpperCase() + name.slice(1)}
          width="120"
          height="120"
          draggable={false}
        />
        {index === 0 && (
          <Badge variant="status" status="featured" className={css.main}>
            Main
          </Badge>
        )}
        {image.quality === "low" && image.status === "uploaded" && (
          <Badge variant="status" status="pending" className={css.quality}>
            Low resolution
          </Badge>
        )}
        <ImageStatus image={image} name={name} onRetry={onRetry} />
        {!readOnly && (
          <div className={css.actions}>
            <ImageActions
              image={image}
              index={index}
              count={count}
              canEdit={canEdit}
              onEdit={onEdit}
              onMove={onMove}
              onRemove={onRemove}
            />
          </div>
        )}
      </div>
    </li>
  );
};

Thumbnail.propTypes = {
  canEdit: PropTypes.bool,
  count: PropTypes.number.isRequired,
  image: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const ImageStrip = function ImageStrip({
  images,
  readOnly = false,
  canEdit,
  onChooseFiles,
  onEdit,
  onMove,
  onRemove,
  onRetry,
}) {
  const inputRef = React.useRef(null);
  const isFull = images.length >= MAX_IMAGES;
  const ids = images.map((image) => image.imageId);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_DRAG }),
    useSensor(TouchSensor, { activationConstraint: TOUCH_DRAG }),
  );

  const drop = ({ active, over }) => {
    const move = moveForDrop({ ids, activeId: active.id, overId: over?.id });

    if (move) onMove(move.from, move.to);
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={drop}
      >
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <ul className={css.list}>
            {images.map((image, index) => (
              <Thumbnail
                key={image.imageId}
                image={image}
                index={index}
                count={images.length}
                readOnly={readOnly}
                canEdit={canEdit(image.imageId)}
                onEdit={onEdit}
                onMove={onMove}
                onRemove={onRemove}
                onRetry={onRetry}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            multiple
            hidden
            onChange={(event) => {
              const chosen = [...event.target.files];
              inputRef.current.value = "";
              onChooseFiles(chosen);
            }}
          />
          <Button
            variant="tonal"
            leading={<AddIcon />}
            disabled={isFull}
            onClick={() => inputRef.current?.click()}
          >
            Add images
          </Button>
          {isFull && (
            <Typography variant="body-sm" color="variant" as="p">
              A product can have up to {MAX_IMAGES} images.
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

ImageStrip.propTypes = {
  canEdit: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChooseFiles: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default ImageStrip;
